import { createFileRoute, Link } from "@tanstack/react-router";
import type { SearchSchemaInput } from "@tanstack/react-router";
import { PageShell } from "@/components/page-shell";
import { RiskBadge } from "@/components/risk-badge";
import { CheckArea } from "@/components/zip-checker";
import { STATES } from "@/lib/states";
import { RISK_LEVELS, riskMeaning } from "@/lib/risk";
import { countyNounForCount } from "@/lib/county-label";
import { CDC_STATUS_SOURCE } from "@/lib/county-status-text";
import { nationalTickTally, stateTickTally } from "@/lib/state-tick-tally";
import { breadcrumbJsonLd } from "@/lib/jsonld";
import { metaDescription, pageTitle } from "@/lib/seo";
import { absUrl } from "@/lib/site";

const NATIONAL = nationalTickTally();
const WHERE = `${NATIONAL.established.toLocaleString("en-US")} counties across ${NATIONAL.states} states${NATIONAL.dc ? " and D.C." : ""}`;
const DESCRIPTION = metaDescription(
  `Lone Star ticks are established in ${WHERE}, CDC records show. See each state's county count, peak season and county map.`,
);

export const Route = createFileRoute("/states/")({
  // "/states?q=Brandon, FL" runs the search on arrival. It is the address the site's
  // WebSite markup gives search engines and assistants for a place search.
  validateSearch: (input: { q?: unknown } & SearchSchemaInput): { q?: string } => {
    // The router parses "?q=30301" as the number 30301; a ZIP code is still a search.
    const raw = typeof input.q === "number" ? String(input.q) : input.q;
    const q = typeof raw === "string" ? raw.trim().slice(0, 100) : "";
    return q ? { q } : {};
  },
  head: () => ({
    meta: [
      { title: pageTitle("Lone Star Ticks by State: CDC County Counts") },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: "Lone Star ticks by state" },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:url", content: absUrl("/states") },
    ],
    links: [{ rel: "canonical", href: absUrl("/states") }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify(
          breadcrumbJsonLd([
            { name: "Home", path: "/" },
            { name: "States", path: "/states" },
          ]),
        ),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          name: "Lone Star ticks by state",
          url: absUrl("/states"),
          description: DESCRIPTION,
          mainEntity: {
            "@type": "ItemList",
            itemListElement: STATES.map((s, i) => ({
              "@type": "ListItem",
              position: i + 1,
              name: `Lone Star ticks in ${s.name}`,
              url: absUrl(`/states/${s.slug}`),
            })),
          },
        }),
      },
    ],
  }),
  component: StatesIndex,
});

/** "42 of 67 counties established" or, where there are none, what CDC does have. */
function tallyLine(abbr: string): string {
  const t = stateTickTally(abbr);
  const nouns = countyNounForCount(abbr);
  if (t.total <= 1) return t.established ? "Established" : t.reported ? "Reported" : "No records";
  if (t.established) return `${t.established} of ${t.total} ${nouns} established`;
  if (t.reported) return `None established; reported in ${t.reported} of ${t.total}`;
  return `No CDC records in any of its ${t.total} ${nouns}`;
}

/** "Peak March – October"; Florida's "Year-round (peak April – August)" reads as a sentence. */
function seasonLine(peakSeason: string): string {
  const yearRound = /^Year-round \(peak (.+)\)$/.exec(peakSeason);
  return yearRound ? `Active year-round, peak ${yearRound[1]}` : `Peak ${peakSeason}`;
}

function StatesIndex() {
  const { q } = Route.useSearch();
  return (
    <PageShell
      crumbs={[
        { name: "Home", path: "/" },
        { name: "States", path: "/states" },
      ]}
      title="Lone Star ticks by state"
      intro={`CDC lists Lone Star ticks as established in ${WHERE}, and has reports from ${NATIONAL.reported.toLocaleString("en-US")} more counties. Find your state for its county map, peak season and what to do.`}
    >
      <section aria-labelledby="search-area" className="max-w-2xl">
        <h2 id="search-area" className="font-display text-xl font-medium text-foreground">
          Or search your ZIP code, town or county
        </h2>
        <div className="mt-3">
          <CheckArea compact initialQuery={q} />
        </div>
      </section>

      <section aria-labelledby="all-states">
        <h2 id="all-states" className="label-caps text-muted-foreground">
          All {STATES.length}, A to Z
        </h2>
        <ul className="mt-3 border-t border-rule/70 sm:columns-2 sm:gap-10 lg:columns-3">
          {STATES.map((s) => (
            <li key={s.slug} className="break-inside-avoid border-b border-border">
              <Link
                to="/states/$state"
                params={{ state: s.slug }}
                className="group block min-h-12 py-2.5"
              >
                <span className="flex items-baseline justify-between gap-3">
                  <span className="font-display text-lg text-foreground group-hover:text-primary">
                    {s.name}
                  </span>
                  <RiskBadge level={s.risk} className="text-xs font-medium text-muted-foreground" />
                </span>
                <span className="mt-0.5 block text-xs leading-snug text-muted-foreground">
                  {tallyLine(s.abbr)}
                  {s.risk !== "low" && <> · {seasonLine(s.peakSeason)}</>}
                </span>
              </Link>
            </li>
          ))}
        </ul>
        <p className="mt-3 text-xs text-muted-foreground">
          County counts:{" "}
          <a
            href={CDC_STATUS_SOURCE.url}
            target="_blank"
            rel="noopener noreferrer"
            className="link print-no-url"
          >
            CDC county data, {CDC_STATUS_SOURCE.year}
            <span className="sr-only"> (opens in a new tab)</span>
          </a>
          . The whole table, state by state, is on{" "}
          <Link to="/$guide" params={{ guide: "where-do-lone-star-ticks-live" }} className="link">
            where Lone Star ticks live
          </Link>
          .
        </p>
      </section>

      <section aria-labelledby="levels-key">
        <h2 id="levels-key" className="label-caps text-muted-foreground">
          What the levels mean
        </h2>
        <dl className="mt-3 grid divide-y divide-border border-y border-border sm:grid-cols-2 sm:divide-y-0 sm:gap-x-10">
          {RISK_LEVELS.map((level) => (
            <div
              key={level}
              className="grid grid-cols-[9.5rem_1fr] gap-3 py-3 sm:border-b sm:border-border"
            >
              <dt>
                <RiskBadge level={level} />
              </dt>
              <dd className="text-sm leading-relaxed text-muted-foreground">
                {riskMeaning[level]}
              </dd>
            </div>
          ))}
        </dl>
      </section>

      <p className="measure text-sm leading-relaxed text-muted-foreground">
        Levels are our summary of CDC tick-surveillance and alpha-gal case data plus peer-reviewed
        research, reviewed June 2026. They describe where ticks are reported, not your personal
        medical risk. Where public data is limited, we say so rather than overstate it.{" "}
        <Link to="/about" hash="how-we-rate-places" className="link">
          How we rate places
        </Link>
        ,{" "}
        <Link to="/$guide" params={{ guide: "tick-season-by-state" }} className="link">
          peak months for every state
        </Link>{" "}
        and{" "}
        <Link to="/sources" className="link">
          every source
        </Link>
        .
      </p>
    </PageShell>
  );
}
